/* eslint-disable react/jsx-no-bind */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  type MRT_TableOptions,
  type MRT_PaginationState,
  type MRT_SortingState,
  Button,
  Box,
  H2,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  TextInput,
  ListDriveInfo,
  MRT_Row,
  RecordingProcessStatus,
  RowActionButton,
  Icon,
  OrganizationRecording,
  ColumnFilterPopover,
  FilterDropDown,
  ListSensorInfo,
  mapCategoryForDisplay,
  useDisclosure,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  EditRecording,
} from '@ouster/webviz-react-components';

import { IconButton, Menu, MenuItem } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { uniq } from 'lodash';
import {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import { MdRefresh, MdClear, MdSearch, MdMoreVert } from 'react-icons/md';
import { TbEyeStar } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import ModalContainer from '../../components/ModalContainer';
import RecordingAccessConfig from '../../components/recordingTable/RecordingAccessConfig';
import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import ReUploadExtrinsic from '../../components/recordingTable/ReUploadExtrinsic';
import { useColumnVisibility } from '../../components/recordingTable/useColumnVisibility';
import useFilters from '../../components/recordingTable/useFilters';
import useOrderBy from '../../components/recordingTable/useOrderBy';
import { usePrivateListQuery } from '../../components/recordingTable/useRecordingListQuery';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../../reducers';
import { RecordingPublicityAccessConfig } from '../../types';
import { DRIVES_PER_PAGE, FILTER_GROUPS } from '../../utils/constants';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import { createPaginationConfig } from '../../utils/tableUtils';

const shortenFilterForPopover = (filters: Record<string, string[]>) => {
  if (filters.data_category) {
    const mappedCategory = filters.data_category.map((category) =>
      mapCategoryForDisplay(category)
    );

    filters.data_category = uniq(mappedCategory);
  }

  return filters;
};

const saveSearchToLocalStorage = (search: string, orgId: number) => {
  const searchKey = `search_${orgId}`;
  localStorage.setItem(searchKey, search);
};

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  copyCloudVizLink: (value: ListDriveInfo) => void;
  copyCloudPublicLink: (value: ListDriveInfo) => void;
  handleDelete: (value: ListDriveInfo) => void;
  reprocessRecording: (value: ListDriveInfo) => void;
  reUploadExtrinsic: (value: ListDriveInfo) => void;
  configRecordingPublicity: (value: ListDriveInfo) => void;
  updateRecordingInfo: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  copyCloudVizLink,
  copyCloudPublicLink,
  handleDelete,
  reprocessRecording,
  reUploadExtrinsic,
  configRecordingPublicity,
  openRecordingDownloadPopover,
  updateRecordingInfo,
}: RowActionsProps) => {
  const { curUserOrg, appUser, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const createClickHandler =
    (handler: (r: ListDriveInfo) => void) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      handler(row.original);
      handleClose();
    };

  const isOwner = appUser?.id === row.original.uploaded_by_id;
  const isAdmin = curUserOrg?.role === 'ADMIN';
  const isFailed = row.original.status === RecordingProcessStatus.Failed;

  const conditionalActions = [
    !isFailed && {
      title: 'Edit File Name',
      icon: <Icon icon="edit" />,
      handler: createClickHandler(updateRecordingInfo),
    },
    isOusterUser &&
      isAdmin && {
        title: 'Configure Sample Access',
        icon: <TbEyeStar size={24} />,
        handler: createClickHandler(configRecordingPublicity),
      },
    isOusterUser &&
      (isAdmin || isOwner) && {
        title: 'Reprocess recording',
        icon: <Icon icon="refresh" />,
        handler: createClickHandler(reprocessRecording),
      },
    isOusterUser &&
      (isAdmin || isOwner) &&
      row.original.sensor_info.length > 1 && {
        title: 'Re-upload extrinsic',
        icon: <Icon icon="upload" />,
        handler: createClickHandler(reUploadExtrinsic),
      },
    (isOwner || isAdmin) && {
      title: 'Delete recording',
      icon: <Icon icon="delete" />,
      handler: createClickHandler(handleDelete),
    },
    row.original.public_url && {
      title: 'Copy public link',
      icon: <Icon icon="public" />,
      handler: createClickHandler(copyCloudPublicLink),
    },
  ].filter(Boolean);

  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      alignItems="center"
      data-testid="private-recordings-row-action-buttons"
    >
      <RowActionButton
        title="Copy the visualization link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudVizLink}
      />
      <RowActionButton
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />

      {conditionalActions.length > 0 && (
        <>
          <IconButton
            aria-label="More actions"
            id="more-actions-button"
            aria-controls={open ? 'more-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
          >
            <MdMoreVert />
          </IconButton>
          <Menu
            id="more-actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={(event: Event) => {
              event.stopPropagation();
              handleClose();
            }}
            MenuListProps={{
              'aria-labelledby': 'more-actions-button',
            }}
          >
            {conditionalActions.map(
              (action) =>
                action && (
                  <MenuItem key={action.title} onClick={action.handler}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {action.icon}
                      {action.title}
                    </Box>
                  </MenuItem>
                )
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};
const DEFAULT_COLUMN_VISIBILITY = {
  start_time: false,
  sdk_fw_branch: false,
  sdk_fw_build_rev: false,
  sdk_fw_image_rev: false,
};

/**
 *
 */
export default function PrivateRecordings() {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  const {
    deleteDrive,
    reprocessDrive,
    setSourceFileInfo,
    setOutputFileInfo,
    updateRecordingTitle,
  } = useStoreActions((actions) => actions.drive);

  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );
  // get filterable options
  const { loadOrgFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { orgFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(orgFilterOptions);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: DRIVES_PER_PAGE,
  });

  const [isReprocessing, setIsReprocessing] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tableContainer = tableContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  /* 
    Control for extrinsic re-upload modal
  */
  const {
    isOpen: isExtrinsicOpen,
    onOpen: onExtrinsicOpen,
    onClose: onExtrinsicClose,
  } = useDisclosure();
  const extrinsicRecordingRef = useRef<ListDriveInfo | undefined>(undefined);

  const {
    isOpen: isRecordingInfoModalOpen,
    onOpen: onRecordingInfoModalOpen,
    onClose: onRecordingInfoModalClose,
  } = useDisclosure();
  const recordingInfoRef = useRef<ListDriveInfo | undefined>(undefined);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orderBy, sortOrder, setOrderBy } = useOrderBy();
  const { filters, setGroupFilters } = useFilters();
  const { showToast } = useChakraHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: 'processed_at', desc: true },
  ]);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    orgId: curUserOrg?.id,
    defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
    configKey: 'drive_table_column_config',
  });

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const { data, meta, isError, isRefetching, isLoading, isSuccess, refetch } =
    usePrivateListQuery(
      curUserOrg,
      orderBy,
      sortOrder,
      pagination.pageIndex,
      pagination.pageSize,
      location.search
    );

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  /* 
    Control for recording publicity configuration modal
  */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();

  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onPublicitySettingCloseHandler = useCallback(() => {
    publicityRecordingRef.current = undefined;
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const firstKey = query.queryKey[0];
          return [
            'shared-from-list',
            'drive-list',
            'private-public-drive-list',
            'public-public-drive-list',
          ].includes(firstKey as string);
        },
      });

      queryClient.refetchQueries({
        queryKey: ['public-public-drive-list'],
      });

      refetch();

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient, refetch]);

  /* 
    We need the full category list for filtering, however, we are showing
    shortened, mapped list in the popover since we need to use the shortened selected list
    when pass to filter column popover.
  */
  const shortenedFilters = shortenFilterForPopover(filters);

  const filterFields = useMemo(
    () => [
      baseRecordingColumnMapping[BaseRecordingTableColumn.STATUS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.DUR],
      baseRecordingColumnMapping[BaseRecordingTableColumn.PRODUCT_TYPE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RESOLUTION],
      baseRecordingColumnMapping[BaseRecordingTableColumn.BEAM_SPACING],
      baseRecordingColumnMapping[BaseRecordingTableColumn.MODE],
      baseRecordingColumnMapping[BaseRecordingTableColumn.FW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.HW],
      baseRecordingColumnMapping[BaseRecordingTableColumn.SN],
      baseRecordingColumnMapping[BaseRecordingTableColumn.TAGS],
      baseRecordingColumnMapping[BaseRecordingTableColumn.CATEGORY],
      baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADER],
      baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDING_TITLE],
    ],
    []
  );

  const filteredData = useMemo(() => {
    // start filter when search term's length is greater than 2
    if (!searchTerm || searchTerm.length <= 2) return data;

    const lowercasedSearchTerm = searchTerm.toLowerCase();

    /* 
      For multi-sensor support, the sensor information are moved to sensor array
      Hence, to filter the data, we need to go deep and check the sensor array as well.
    */
    return data.filter((item) =>
      filterFields.some((field) => {
        if (item.sensor_info[0] && field in item.sensor_info[0]) {
          // Field belongs to ListSensorInfo, check inside sensor_info
          return item.sensor_info?.some((sensor) =>
            sensor[field as keyof ListSensorInfo]
              ?.toString()
              .toLowerCase()
              .includes(lowercasedSearchTerm)
          );
        }
        // Field belongs to ListDriveInfo, check on item
        return item[field as keyof ListDriveInfo]
          ?.toString()
          .toLowerCase()
          .includes(lowercasedSearchTerm);
      })
    );
  }, [data, filterFields, searchTerm]);

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        copyCloudVizLink={copyCloudVizLink}
        copyCloudPublicLink={copyCloudPublicLink}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
        handleDelete={handleDelete}
        reprocessRecording={reprocessRecording}
        configRecordingPublicity={configRecordingPublicity}
        updateRecordingInfo={updateRecordingInfo}
        reUploadExtrinsic={reUploadExtrinsic}
      />
    ),
    enableColumnPinning: false,
    enableHiding: true,
    enableTopToolbar: true,
    rowCount: meta?.totalCount ?? 0,
    state: {
      isLoading: isLoading || isReprocessing,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars: isLoading || isRefetching,
      showSkeletons: isLoading || isRefetching || isReprocessing,
      sorting,
      columnVisibility,
    },
    ...createPaginationConfig(meta, pagination),
  };

  useEffect(() => {
    if (sorting.length) {
      const { id: accessorKey, desc } = sorting[0];
      if (
        accessorKey ===
          baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDED] ||
        accessorKey ===
          baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADED]
      ) {
        const sortDirection = desc ? 'desc' : 'asc';

        setOrderBy(accessorKey, sortDirection);
      }
    }
  }, [setOrderBy, sorting]);

  /* 
    When we visit this page via nav menu, there is no search query
    in this case, we won't want to update our local storage with the empty query
    we will add the default query to the search and make decision after
    about whether we need to update query in local storage
  */
  const [isDefaultQueryReady, setIsDefaultQueryReady] = useState(false);
  useLayoutEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    if (location.search === '') {
      navigate(
        { search: 'order_by=processed_at&sort_order=desc' },
        { replace: true }
      );
    } else {
      setIsDefaultQueryReady(true);
    }
  }, [location.search, navigate]);

  /* 
    we will update local storage when
    - data is successfully fetched (live or cached)
    - and default query is setup so no accidentally wipe useful saved query
    - it doesn't happen during switch organization
  */
  useEffect(() => {
    if (
      curUserOrg?.id &&
      isDefaultQueryReady &&
      isSuccess &&
      !sessionStorage.getItem('orgChanged')
    ) {
      saveSearchToLocalStorage(location.search, curUserOrg.id);
    }
  }, [curUserOrg?.id, isDefaultQueryReady, isSuccess, location.search]);

  useEffect(() => {
    /* 
      We only use the filter from storage when it is not default filter
    */
    if (location.search !== '?order_by=processed_at&sort_order=desc') return;
    const searchKey = `search_${curUserOrg?.id}`;
    const storedSearch = localStorage.getItem(searchKey);

    if (storedSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: storedSearch,
        },
        { replace: true }
      );
    }
  }, [curUserOrg?.id, navigate, location.pathname, location.search]);

  /* 
    Whenever user switch organization. We need to clear the previous filters and invalidate query
  */
  useEffect(() => {
    if (curUserOrg?.id && sessionStorage.getItem('orgChanged')) {
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
      // get search query based on orgId
      const storedSearch = localStorage.getItem(`search_${curUserOrg.id}`);

      navigate({
        pathname: location.pathname,
        search: storedSearch || '',
      });
      sessionStorage.removeItem('orgChanged');
    }
  }, [curUserOrg?.id, location.pathname, navigate, queryClient]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadOrgFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadOrgFilterOptions]);

  /* 
    not open the viz page unless it is viewable
  */
  function openVizPage(recording: ListDriveInfo) {
    if (isLoading) return;
    if (recording.status !== RecordingProcessStatus.Processing) {
      const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function copyCloudVizLink(recording: ListDriveInfo) {
    const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
    copyToClipboard(`${window.location.origin}${url}`);
  }

  function copyCloudPublicLink(recording: ListDriveInfo) {
    if (recording.public_url) copyToClipboard(recording.public_url);
  }

  async function reprocessRecording(recording: ListDriveInfo) {
    try {
      if (!curUserOrg?.id) return;
      setIsReprocessing(true);
      await reprocessDrive({ orgId: curUserOrg.id, driveId: recording.id });
      refetch();
      showToast({
        title: '',
        description: `Reprocessing recording ${recording.id}`,
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to reprocess recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsReprocessing(false);
    }
  }

  function reUploadExtrinsic(recording: ListDriveInfo) {
    extrinsicRecordingRef.current = recording;
    onExtrinsicOpen();
  }

  const [isUpdatingRecording, setIsUpdatingRecording] = useState(false);

  function updateRecordingInfo(recording: ListDriveInfo) {
    recordingInfoRef.current = recording;
    onRecordingInfoModalOpen();
  }
  async function submitRecordingInfoChange(recording: ListDriveInfo) {
    try {
      if (!curUserOrg?.id || !recording.recording_title) return;
      setIsUpdatingRecording(true);
      await updateRecordingTitle({
        orgId: curUserOrg.id,
        driveId: recording.id as number,
        title: recording.recording_title,
      });

      // instead of refetch the whole list, we update the specific item in the cache
      const queryKey = [
        'drive-list',
        curUserOrg.id,
        orderBy,
        sortOrder,
        pagination.pageIndex,
        pagination.pageSize,
        location.search,
      ];

      queryClient.setQueryData<{ data: ListDriveInfo[] }>(
        queryKey,
        (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            data: oldData.data.map((item: ListDriveInfo) =>
              item.id === recording.id
                ? { ...item, recording_title: recording.recording_title }
                : item
            ),
          };
        }
      );

      showToast({
        title: '',
        description: `Recording title has been updated`,
        status: 'success',
      });

      onRecordingInfoModalClose();
      recordingInfoRef.current = undefined;
    } catch (error) {
      showToast({
        title: 'Failed to update recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsUpdatingRecording(false);
    }
  }

  function configRecordingPublicity(recording: ListDriveInfo) {
    publicityRecordingRef.current = {
      organization_id: recording.organization_id,
      id: Number(recording.id),
      publication_uuid: recording.publication_uuid,
      data_category: recording.data_category,
      sensor_info: recording.sensor_info,
      start_time: recording.start_time,
      recording_title: recording.recording_title,
    };

    onPublicitySettingOpen();
  }

  function handleDelete(drive: ListDriveInfo) {
    const onConfirm = async () => {
      if (!curUserOrg?.id) return;
      try {
        await deleteDrive({ orgId: curUserOrg.id, driveId: drive.id });
        // reload the filter options in case the delete record remove category and tags
        // existed in previous one
        await loadOrgFilterOptions(curUserOrg.id);
        refetch();

        // when an ouster published drive is deleted. invalidate sample data query
        if (drive.publication_uuid && curUserOrg.id === 1) {
          queryClient.invalidateQueries({
            queryKey: ['public-public-drive-list'],
          });
        }

        // from list page, we won't be able to know whether the drive is shared or not
        // so we need to invalidate the shared list query any way so that the deleted drive
        // won't be shown in the shared list
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      } catch (error) {
        showToast({
          title: 'Failed to delete recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };

    if (curUserOrg?.id) {
      openConfirmDialog({
        onConfirm,
        isPublic: !!drive.public_url,
      });
    }
  }
  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);

  function openRecordingDownloadPopover(
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) {
    if (!event) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.id.toString());
    downloadRecordingTitleRef.current = recording.recording_title;
  }

  const closeDownloadMenu = useCallback(() => {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  }, [setOutputFileInfo, setSourceFileInfo]);

  function clearAllFilters() {
    if (curUserOrg?.id) {
      localStorage.removeItem(`search_${curUserOrg.id}`);
    }

    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  }

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  if (!curUserOrg) return null;

  return (
    <>
      <Box
        p="25px 36px 0px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto auto 1fr"
        gap="1rem"
        data-testid="private-recordings-root"
      >
        <Box display="grid" rowGap="1rem">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            mb={6}
          >
            <H2 fontWeight="medium" fontSize={30}>
              Recordings
            </H2>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => refetch()}
              data-testid="private-recordings-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>

          {/* filters and search controls */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            mr="50px"
            zIndex={2}
            data-testid="private-recordings-filters"
          >
            <InputGroup
              maxW="25ch"
              data-testid="private-recordings-search-group"
            >
              <InputLeftElement pointerEvents="none">
                <MdSearch size={18} />
              </InputLeftElement>
              <TextInput
                pl="2.5rem"
                pr="3.5rem"
                placeholder="Search ..."
                type="search"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                data-testid="private-recordings-search-input"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setSearchTerm('')}
                  data-testid="private-recordings-search-clear"
                >
                  <MdClear size={16} />
                </IconButton>
              </InputRightElement>
            </InputGroup>

            {FILTER_GROUPS.map((groupedFilter) => (
              <FilterDropDown
                title={groupedFilter.title}
                filterKey={groupedFilter.filterKeys}
                filterOptions={filterOptions}
                setGroupFilters={setGroupFilters}
                selectedFilters={filters}
                key={groupedFilter.title}
                data-testid={`private-recordings-filter-${groupedFilter.title
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
              />
            ))}

            {/* 
            Clear all filters button
          */}
            {totalSelectedFilters > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                gap={1}
                onClick={() => clearAllFilters()}
                fontSize="12px"
                data-testid="private-recordings-clear-filters"
              >
                <MdClear size={18} /> Clear Filters
              </Button>
            )}
          </Box>
        </Box>
        <Box
          overflow="hidden"
          position="relative"
          bg="rgb(29, 29, 29)"
          mt="-56px"
          zIndex={1}
          ref={tableContainerRef}
          data-testid="private-recordings-table-container"
        >
          <OrganizationRecording
            key={`${curUserOrg.id}`}
            filters={filters || {}}
            maxHeight="calc(100vh - 320px)"
            filteredData={filteredData}
            setFilterAnchorEl={setFilterAnchorEl}
            setSelectedColKeys={setSelectedColKeys}
            openVizPage={openVizPage}
            customTableOptions={customTableOptions}
            isOuster={isOusterUser}
          />
        </Box>
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={closeDownloadMenu}
          downloadRecordingId={downloadRecordingId}
          orgId={curUserOrg.id}
          recordingTitle={downloadRecordingTitleRef.current}
          data-testid="private-recordings-download-popover"
        />
      )}

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={shortenedFilters}
          setGroupFilters={setGroupFilters}
          data-testid="private-recordings-column-filter-popover"
        />
      )}

      {extrinsicRecordingRef.current && (
        <ModalContainer
          isOpen={isExtrinsicOpen}
          onClose={onExtrinsicClose}
          header="Re-upload extrinsic file"
          footer={null}
          content={
            <ReUploadExtrinsic
              orgId={curUserOrg.id}
              recording={extrinsicRecordingRef.current}
              reprocessRecording={reprocessRecording}
              onClose={onExtrinsicClose}
            />
          }
          data-testid="private-recordings-extrinsic-modal"
        />
      )}

      {recordingInfoRef.current && (
        <EditRecording
          recording={recordingInfoRef.current}
          onClose={onRecordingInfoModalClose}
          onSubmit={submitRecordingInfoChange}
          isLoading={isUpdatingRecording}
          isRecordingInfoModalOpen={isRecordingInfoModalOpen}
          heading="Edit Recording"
          data-testid="private-recordings-edit-recording-modal"
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
          data-testid="private-recordings-access-config-modal"
        />
      )}
    </>
  );
}
